/**
 * SOP Runtime Engine
 *
 * Phase 2 — the consumer of compiled SOPs. Mutation hooks call
 * `evaluateOnEvent(...)` after persisting a write, and the engine:
 *   1. Picks the SOPs whose scope and trigger match the event
 *   2. Runs the compiled rule (deterministic) against the payload, OR if the
 *      SOP is flagged advisory, hands the payload + NL body to the LLM
 *   3. Writes a row to sop_evaluations
 *   4. Drops a notification per failure for in_app channels
 *   5. Returns findings so the caller can surface them inline / block / warn
 *
 * Mutation callers stay non-blocking by default: the engine never throws on
 * non-block-severity findings, and even block-severity findings are returned
 * as data — it's the caller's job to decide what to do (revert, surface,
 * etc.). The "block" semantic is enforced by `assertNoBlockingFindings(...)`
 * which the caller invokes before persisting the write.
 *
 * Why a cache: hot paths (every Risk save, every Project status flip) can't
 * pay a Supabase round-trip for the SOP list on every call. We TTL-cache the
 * enabled SOP set for 30s and let `invalidateSopCache()` punch a hole when
 * the user saves a new SOP.
 */

import { listSops, recordEvaluation } from '@/services/sopService'
import { evaluateRule } from '@/services/sopCompiler'
import { getAiProvider } from '@/services/aiService'
import type { AiMessage } from '@/types/ppm'
import { generateId } from '@/lib/utils'
import type {
  Sop,
  SopFinding,
  SopTriggerKind,
  SopScopeKind,
} from '@/types/sop'
import { writeSopNotification, dispatchExternalChannels } from '@/services/notificationService'
import { writeAuditEntry } from '@/services/auditLogService'

// ── Cache ────────────────────────────────────────────────────────────

interface CacheEntry {
  loadedAt: number
  sops: Sop[]
}

let cache: CacheEntry | null = null
const CACHE_TTL_MS = 30_000

/**
 * Drop the SOP cache. Call this after any write that changes the active SOP
 * set (create / update / delete / toggle).
 */
export function invalidateSopCache(): void {
  cache = null
}

async function getActiveSops(): Promise<Sop[]> {
  const now = Date.now()
  if (cache && now - cache.loadedAt < CACHE_TTL_MS) return cache.sops
  try {
    const all = await listSops()
    cache = { loadedAt: now, sops: all.filter((s) => s.enabled) }
    return cache.sops
  } catch {
    // Don't bring down the mutation path because the SOP table is offline /
    // RLS misconfigured / migration not applied. Treat as no SOPs.
    return []
  }
}

// ── Event shape ──────────────────────────────────────────────────────

export interface SopEvent {
  /** What kind of write happened. */
  trigger: SopTriggerKind
  /** The entity table-name token used in scope_target (e.g. 'projects'). */
  entityType: string
  /** The row's primary key, for the audit row. */
  entityId: string
  /** Optional display name for the notification. */
  entityName?: string
  /** The full payload, in camelCase. The rule evaluator dotted-paths into it. */
  payload: Record<string, unknown>
  /** Optional event-target token for `scope_kind = 'event'` SOPs (e.g. 'workflow.stage.approved'). */
  eventTarget?: string
  /** Optional portfolio context for the notification. */
  portfolioId?: string
  portfolioName?: string
  /** Optional user identity for ran_by + requested_by. */
  actor?: string
  actorName?: string
}

export interface SopFindingWithSop {
  sop: Sop
  findings: SopFinding[]
}

// ── Scope matching ───────────────────────────────────────────────────

function scopeMatches(sop: Sop, event: SopEvent): boolean {
  // event-scope: target string must literally match the eventTarget on the event.
  if (sop.scopeKind === 'event') {
    return !!event.eventTarget && sop.scopeTarget === event.eventTarget
  }
  // global: any entity touched by this event qualifies.
  if (sop.scopeKind === 'global') return true
  // entity-scope: target must equal the entity table token.
  if (sop.scopeKind === 'entity') return sop.scopeTarget === event.entityType
  // field-scope: target is "<entity>.<field>" — check the entity prefix only.
  // Whether the specific field is dirty is the caller's concern; we still run
  // the rule because evaluating it on a non-dirty entity is harmless.
  if (sop.scopeKind === 'field') {
    if (!sop.scopeTarget) return false
    const dotIdx = sop.scopeTarget.indexOf('.')
    const entityPrefix = dotIdx < 0 ? sop.scopeTarget : sop.scopeTarget.slice(0, dotIdx)
    return entityPrefix === event.entityType
  }
  return false
}

function triggerMatches(sop: Sop, event: SopEvent): boolean {
  // 'manual' SOPs only run on explicit manual invocation; the engine isn't it.
  if (sop.triggerKind === 'manual') return event.trigger === 'manual'
  return sop.triggerKind === event.trigger
}

// ── Advisory LLM eval ────────────────────────────────────────────────

const ADVISORY_SYSTEM_PROMPT = `You are a Standard Operating Procedure evaluator.

You receive a rule in plain English and a JSON payload representing an
entity (project, risk, contract, etc.). Decide whether the rule holds for
the payload. Return ONLY a single JSON object — no prose, no markdown:

  { "passed": boolean, "findings": [ { "clause": string, "message": string, "fieldPath"?: string } ] }

Rules:
- "passed" is true ONLY when every requirement in the SOP holds.
- "findings" lists each violated requirement, one entry per requirement.
- Keep "message" under 140 characters and concrete; refer to the actual value.
- Use "fieldPath" when the violation maps to a single field in the payload.
`

async function advisoryEvaluate(sop: Sop, payload: Record<string, unknown>): Promise<SopFinding[]> {
  const provider = getAiProvider()
  if (provider.name === 'mock') {
    // Mock won't do anything useful; surface a single info finding so authors
    // know advisory mode requires a real LLM provider.
    return [
      {
        clause: 'advisory',
        message: 'Advisory mode needs the AI assistant, which is not turned on for this workspace. The AI evaluation was skipped.',
      },
    ]
  }
  const messages: AiMessage[] = [
    {
      id: generateId(),
      role: 'system',
      content: ADVISORY_SYSTEM_PROMPT,
      timestamp: new Date().toISOString(),
    },
    {
      id: generateId(),
      role: 'user',
      content: [`SOP: ${sop.nlBody.trim()}`, '', 'Payload:', JSON.stringify(payload, null, 2)].join('\n'),
      timestamp: new Date().toISOString(),
    },
  ]
  try {
    const response = await provider.sendMessage(messages)
    const parsed = parseAdvisoryResponse(response.content)
    return parsed.findings
  } catch (err) {
    return [
      {
        clause: 'advisory',
        message: `Advisory evaluation failed: ${err instanceof Error ? err.message : String(err)}`,
      },
    ]
  }
}

function parseAdvisoryResponse(text: string): { passed: boolean; findings: SopFinding[] } {
  const fenced = text.match(/```(?:json)?\s*([\s\S]+?)```/)
  const body = fenced?.[1] ?? text
  try {
    const obj = JSON.parse(body.trim()) as { passed?: boolean; findings?: SopFinding[] }
    return {
      passed: !!obj.passed,
      findings: Array.isArray(obj.findings) ? obj.findings : [],
    }
  } catch {
    return { passed: false, findings: [{ clause: 'advisory', message: 'LLM returned unparseable advisory output.' }] }
  }
}

// ── Main entry point ─────────────────────────────────────────────────

export interface EvaluateOptions {
  /** Skip writing sop_evaluations rows. Used by the simulator + the cron's dry-run flag. */
  skipPersistence?: boolean
  /** Skip emitting notifications. Useful for high-frequency or background sweeps. */
  skipNotifications?: boolean
}

/**
 * Run every SOP that matches the event against the payload.
 *
 * Wraps every per-SOP run in try/catch so one broken SOP doesn't blast the
 * mutation. Logs to console for now; the audit chain integration belongs in
 * a later pass.
 */
export async function evaluateOnEvent(
  event: SopEvent,
  opts: EvaluateOptions = {},
): Promise<SopFindingWithSop[]> {
  const sops = await getActiveSops()
  const out: SopFindingWithSop[] = []

  for (const sop of sops) {
    if (!scopeMatches(sop, event)) continue
    if (!triggerMatches(sop, event)) continue

    try {
      const findings = sop.advisory
        ? await advisoryEvaluate(sop, event.payload)
        : sop.compiledRule
          ? evaluateRule(sop.compiledRule, event.payload)
          : [] // Uncompiled non-advisory SOP: skip silently. Author needs to compile.

      if (findings.length > 0) {
        out.push({ sop, findings })
      }

      if (!opts.skipPersistence) {
        await recordEvaluation({
          sopId: sop.id,
          entityType: event.entityType,
          entityId: event.entityId,
          passed: findings.length === 0,
          findings,
          payloadSnapshot: clampPayload(event.payload),
          triggeredBy: event.trigger,
          ranBy: event.actor,
        }).catch((err) => {
          // Recording failures shouldn't block. Log and move on.
          console.warn('[sopEngine] failed to record evaluation', err)
        })

        // Mirror the finding into the generic audit_log so the timeline
        // reader picks it up. We only log when there's something to say
        // (failure) — silent passes would flood the log with noise.
        if (findings.length > 0) {
          await writeAuditEntry({
            kind: 'sop.finding',
            entityType: event.entityType,
            entityId: event.entityId,
            actor: event.actor,
            actorName: event.actorName,
            action: `sop:${sop.severity}`,
            summary: `${sop.name}: ${findings.map((f) => f.message).join(' · ')}`.slice(0, 500),
            severity: sop.severity,
            portfolioId: event.portfolioId,
            payload: {
              sopId: sop.id,
              sopName: sop.name,
              scopeKind: sop.scopeKind,
              scopeTarget: sop.scopeTarget,
              triggerKind: event.trigger,
              advisory: sop.advisory,
              findings,
            },
          }).catch((err) => {
            console.warn('[sopEngine] failed to write audit entry', err)
          })
        }
      }

      if (!opts.skipNotifications && findings.length > 0) {
        for (const channel of sop.notifyChannels) {
          if (channel.kind === 'in_app') {
            await writeSopNotification({
              sop,
              event,
              findings,
              roles: channel.roles ?? [],
            }).catch((err) => {
              console.warn('[sopEngine] failed to write notification', err)
            })
          }
        }
        // External channels (email / slack) batch through the dispatcher in
        // one call so the engine still finishes quickly even on a multi-
        // channel SOP. The dispatcher silently no-ops in the browser
        // (no secret), so this is effectively a server-side path that the
        // cron exercises.
        const externals = sop.notifyChannels.filter((c) => c.kind === 'email' || c.kind === 'slack')
        if (externals.length > 0) {
          await dispatchExternalChannels({
            sop,
            event,
            findings,
            channels: externals,
          }).catch((err) => {
            console.warn('[sopEngine] failed to dispatch external channel(s)', err)
          })
        }
      }
    } catch (err) {
      console.error(`[sopEngine] SOP "${sop.name}" (${sop.id}) threw — skipping`, err)
    }
  }

  return out
}

/**
 * Throws an SopBlockedError if any finding came from a `severity='block'`
 * SOP. Mutation callers invoke this AFTER evaluateOnEvent to enforce hard
 * gates. The error carries the offending findings so the caller can render
 * them.
 */
export class SopBlockedError extends Error {
  blockedBy: SopFindingWithSop[]
  constructor(blockedBy: SopFindingWithSop[]) {
    super(
      blockedBy
        .map((b) => `${b.sop.name}: ${b.findings.map((f) => f.message).join('; ')}`)
        .join(' · '),
    )
    this.name = 'SopBlockedError'
    this.blockedBy = blockedBy
  }
}

export function assertNoBlockingFindings(results: SopFindingWithSop[]): void {
  const blocking = results.filter((r) => r.sop.severity === 'block')
  if (blocking.length > 0) throw new SopBlockedError(blocking)
}

// ── Helpers ──────────────────────────────────────────────────────────

/**
 * Trim a payload before snapshotting so we don't write huge rows into
 * sop_evaluations. Caps total JSON-encoded size at ~8 KB.
 */
function clampPayload(payload: Record<string, unknown>): Record<string, unknown> {
  const MAX = 8 * 1024
  let json: string
  try {
    json = JSON.stringify(payload)
  } catch {
    return { _note: 'payload not JSON-serializable' }
  }
  if (json.length <= MAX) return payload
  return { _truncated: true, head: json.slice(0, MAX) }
}

// ── Convenience helpers for callers ──────────────────────────────────

/**
 * Common helper: run engine, surface block-severity findings as an error
 * the caller can catch and revert on, and return non-block findings as
 * data. Use from any write path where SOPs may need to gate.
 *
 * The standard pattern from a mutation:
 *
 *   await runSopGate({
 *     trigger: 'on_save',
 *     entityType: 'projects',
 *     entityId: project.id,
 *     payload: project,
 *     actor: currentUser?.id,
 *   })
 *
 * Returns the warn/info findings (caller decides whether to toast).
 */
export async function runSopGate(event: SopEvent): Promise<SopFindingWithSop[]> {
  const results = await evaluateOnEvent(event)
  assertNoBlockingFindings(results)
  return results.filter((r) => r.sop.severity !== 'block')
}

/**
 * Scope kind exported so external surfaces (dashboard cards) can build
 * filters without re-importing the type module.
 */
export type { Sop, SopFinding, SopScopeKind, SopTriggerKind }
